import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import _ from 'lodash';

import { getBrandDetails } from '../services/brand-service';
import { revertAll } from '../store/actions';
import { persistor, RootState, store } from '../store/index';
import { hideLoader, showLoader } from '../store/reducers/appSlice';
import { setBrandDetails } from '../store/reducers/brandSlice';
import { updateLoginStatus, updateLoginUserDetails } from '../store/reducers/userSlice';
import { clearStoragePreservingMigrationCookies } from '../utils/migration-cookies';
import { NavigationConfig } from '../utils/navigation-constants';

import { api_urls } from './api-urls';
import { app_config } from './app-config';

// Same as useSessionTimeout: only attempt refresh when user has been active within this window
const INACTIVITY_SESSION_DURATION = 5 * 60 * 1000; // 5 minutes

class Server {
    api_urls = api_urls;
    axiosOptions: AxiosRequestConfig = {
        timeout: 120000,
        transformRequest: [this.transformRequest],
        withCredentials: true,
    };
    ContentHeaders = {
        Json: 'application/json',
        FormData: 'multipart/form-data',
        Plain: 'text/plain',
    };

    BaseDomain = {
        Api: app_config.apiDomain,
    };
    constructor() {
        const apiDomain = this.BaseDomain.Api;
        let refreshPromise: Promise<{ token: string; refreshToken: string } | null> | null = null;

        const hasTokenExpiredMarker = (payload: unknown): boolean => {
            if (!payload || typeof payload !== 'object') return false;

            const p = payload as Record<string, unknown>;
            const nested = p['data'] && typeof p['data'] === 'object' ? (p['data'] as Record<string, unknown>) : {};

            const topErrorCode = String(p['errorCode'] || p['code'] || '').toUpperCase();
            const nestedErrorCode = String(nested['errorCode'] || nested['code'] || '').toUpperCase();
            const message = String(p['message'] || p['error'] || nested['message'] || '').toLowerCase();

            return (
                topErrorCode === 'TOKEN_EXPIRED' ||
                nestedErrorCode === 'TOKEN_EXPIRED' ||
                message.includes('token is expired') ||
                message.includes('token expired') ||
                message.includes('jwt expired') ||
                message.includes('session is over') ||
                message.includes('session expired')
            );
        };

        const performLogout = (toastMessage: string) => {
            // Store toast message in sessionStorage to show after redirect
            sessionStorage.setItem('logoutToastMessage', toastMessage);

            // Clear all state immediately
            store.dispatch(revertAll());
            store.dispatch(updateLoginStatus(false));
            persistor.purge();
            clearStoragePreservingMigrationCookies();

            // Redirect to login immediately (cookies preserved so user lands on login page)
            const loginPath =
                (process.env.PUBLIC_URL || '').replace(/\/$/, '') + `${NavigationConfig.Login}?source=login`;
            window.location.replace(loginPath || `${NavigationConfig.Login}?source=login`);
        };

        const refreshAccessToken = (): Promise<{ token: string; refreshToken: string } | null> => {
            if (refreshPromise) return refreshPromise;
            const refreshTokenValue = store.getState().user.refreshToken;
            if (!refreshTokenValue) return Promise.resolve(null);
            refreshPromise = axios({
                method: 'POST',
                url: api_urls.RefreshToken,
                baseURL: apiDomain,
                headers: {
                    Authorization: refreshTokenValue,
                },
            })
                .then(res => {
                    const data = res?.data;
                    const newToken = data?.data?.token || data?.token || data?.accessToken;
                    const newRefreshToken = data?.data?.refreshToken || data?.refreshToken;
                    if (newToken && newRefreshToken) {
                        return { token: newToken, refreshToken: newRefreshToken };
                    }
                    return null;
                })
                .catch(() => null)
                .finally(() => {
                    refreshPromise = null;
                });
            return refreshPromise;
        };

        const handle401 = (error: AxiosError): Promise<unknown> | undefined => {
            const responseData = error?.response?.data as Record<string, unknown> | undefined;
            const msg = String(responseData?.message ?? '').toLowerCase();
            const isInactiveAccount = msg.includes('inactive');
            const isUserNotFound = msg.includes('user not found');
            const toastMessage = isInactiveAccount
                ? 'User account is inactive'
                : 'Your session has expired. Please login again.';
            const requestUrl = String(error?.config?.url || '');
            const isRefreshRequest = requestUrl.includes(api_urls.RefreshToken) || requestUrl.includes('/auth/refresh');

            if (isRefreshRequest) {
                return undefined;
            }
            if (isInactiveAccount) {
                performLogout(toastMessage);
                return undefined;
            }
            if (isUserNotFound) {
                performLogout('User not found. Please login again.');
                return undefined;
            }

            const refreshTokenValue = store.getState().user.refreshToken;
            const lastActivityTimeStr = localStorage.getItem('lastActivityTime');
            const lastActivityTime = lastActivityTimeStr ? parseInt(lastActivityTimeStr, 10) : null;
            const isUserActive =
                lastActivityTime != null && Date.now() - lastActivityTime < INACTIVITY_SESSION_DURATION;

            if (!refreshTokenValue || !isUserActive) {
                performLogout(toastMessage);
                return undefined;
            }

            return refreshAccessToken()
                .then(tokens => {
                    if (!tokens) {
                        performLogout(toastMessage);
                        return Promise.reject(responseData || error);
                    }
                    const currentState = store.getState() as RootState;
                    store.dispatch(
                        updateLoginUserDetails({
                            token: tokens.token,
                            refreshToken: tokens.refreshToken,
                            user: currentState.user.userDetails ?? {},
                        }),
                    );
                    const retryConfig = {
                        ...error?.config,
                        headers: { ...error?.config?.headers, Authorization: `Bearer ${tokens.token}` },
                    };
                    return axios.request(retryConfig);
                })
                .catch(err => {
                    performLogout(toastMessage);
                    return Promise.reject(err);
                });
        };

        const handle403 = (): void => {
            store.dispatch(revertAll());
            store.dispatch(updateLoginStatus(false));
            persistor.purge();
            clearStoragePreservingMigrationCookies();
            getBrandDetails(false)
                .then(response => {
                    if (response.statusCode === 200 && response.data) {
                        store.dispatch(setBrandDetails(response.data));
                    }
                })
                .catch(err => {
                    console.error('Error fetching brand details after logout:', err);
                });
            const loginPath =
                (process.env.PUBLIC_URL || '').replace(/\/$/, '') + `${NavigationConfig.Login}?source=login`;
            window.location.replace(loginPath || `${NavigationConfig.Login}?source=login`);
        };

        const normalizeErrorMessage = (error: AxiosError): void => {
            if (error.message === 'timeout of 120000ms exceeded') {
                error.message = 'Request has been timed out. Please try again';
            } else if (error.message === 'Network Error') {
                error.message = navigator.onLine
                    ? 'We are unable to reach the server. Please try later.'
                    : 'Please check your internet connection.';
            }
        };

        // Request interceptor to reset session activity on API calls
        axios.interceptors?.request.use(
            config => {
                // Reset session activity time on any API call
                const lastActivityTime = localStorage.getItem('lastActivityTime');
                if (lastActivityTime) {
                    const resetActivity = () => {
                        const now = Date.now();
                        localStorage.setItem('lastActivityTime', now.toString());
                    };
                    resetActivity();
                }
                return config;
            },
            error => {
                return Promise.reject(error);
            },
        );

        axios.interceptors?.response.use(
            response => {
                store.dispatch(hideLoader());
                if (hasTokenExpiredMarker(response?.data)) {
                    performLogout('Your session has expired. Please login again.');
                    return Promise.reject(response?.data);
                }
                const now = Date.now();
                localStorage.setItem('lastActivityTime', now.toString());
                return response;
            },
            (error: AxiosError) => {
                store.dispatch(hideLoader());
                const responseDataAnyStatus = error?.response?.data;
                const status = error?.response?.status;

                // Force-logout on TOKEN_EXPIRED for non-401 responses (401s go through handle401 for refresh flow)
                if (status !== 401 && hasTokenExpiredMarker(responseDataAnyStatus)) {
                    performLogout('Your session has expired. Please login again.');
                    return Promise.reject(responseDataAnyStatus || error);
                }

                if (status === 401) {
                    const result = handle401(error);
                    if (result !== undefined) return result;
                } else if (status === 403) {
                    error.message = 'Please login to continue..';
                    handle403();
                }

                normalizeErrorMessage(error);
                return Promise.reject(error.response ? error.response.data : error);
            },
        );
    }

    transformRequest(data: unknown) {
        // If data is undefined or null, return as-is
        if (data === undefined || data === null) {
            return data;
        }
        // If data is FormData, return as-is
        if (data instanceof FormData) {
            return data;
        }
        // If data is a string (JSON stringified), try to parse and clean it
        if (typeof data === 'string') {
            try {
                const parsedData = JSON.parse(data);
                const temp = _.clone(parsedData);
                _.forOwn(parsedData, (value, key) => {
                    if (typeof key === 'string' && key.indexOf('__') > -1) {
                        delete temp[key];
                    }
                });
                return JSON.stringify(temp);
            } catch {
                // If parsing fails, return original data
                return data;
            }
        }
        // For other types, return as-is
        return data;
    }

    getHeadersByType(
        headerType: string,
        domain: string,
        customHeaders?: Record<string, string>,
    ): Record<string, string> {
        let data: { [key: string]: string } = {};
        switch (headerType) {
            case api_service.ContentHeaders.Json: {
                data['Content-Type'] = 'application/json';
                break;
            }
            case api_service.ContentHeaders.Plain: {
                data['Content-Type'] = 'text/plain';
                break;
            }
            case api_service.ContentHeaders.FormData: {
                data['Content-Type'] = 'multipart/form-data';
                break;
            }
            default:
                data['Content-Type'] = 'application/json';
                break;
        }
        data['Authorization'] = 'Bearer ' + store.getState().user.accessToken;
        data['X-User-Region'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Always send the real portal origin so backend can resolve the correct tenant/brand.
        let portalOrigin = '';
        if (typeof window !== 'undefined' && window.location) {
            portalOrigin = window.location.origin;
        } else if (process.env.REACT_APP_X_DOMAIN) {
            // Fallback for non-browser contexts.
            portalOrigin = process.env.REACT_APP_X_DOMAIN.trim();
        }
        // Strip trailing slashes without a regex to avoid any backtracking concerns
        // (linear-time scan from the end of the string).
        let endIndex = portalOrigin.length;
        while (endIndex > 0 && portalOrigin.charCodeAt(endIndex - 1) === 47 /* '/' */) {
            endIndex--;
        }
        portalOrigin = portalOrigin.slice(0, endIndex);

        // Send portal origin via x-domain header for backend to identify the portal
        if (portalOrigin) {
            data['x-domain'] = portalOrigin;
        }

        data = _.extend({}, data, customHeaders);
        return data;
    }

    post = (data: {
        endPoint: string;
        payLoad?: unknown;
        domain?: string;
        headerType?: string;
        customHeaders?: Record<string, string>;
        showLoader?: boolean;
    }) => {
        let payLoadData: FormData | string | undefined;
        if (data.payLoad instanceof FormData) {
            payLoadData = data.payLoad;
        } else if (data.payLoad !== undefined && data.payLoad !== null) {
            payLoadData = JSON.stringify(data.payLoad);
        } else {
            payLoadData = undefined;
        }
        const { domain, headerType } = this.applyDefaultRequestOptions(data);
        this.dispatchLoaderIfNeeded(data);

        return axios.post(data.endPoint, payLoadData, {
            timeout: this.axiosOptions.timeout,
            transformRequest: this.axiosOptions.transformRequest,
            baseURL: domain,
            headers: this.getHeadersByType(headerType, domain, data.customHeaders),
        });
    };

    isNetworkError = (error: unknown) => {
        const e = error as { response?: unknown; code?: string };
        return !e.response && e.code !== 'ECONNABORTED';
    };

    retry = (config: AxiosRequestConfig) => {
        return axios(config);
    };

    // Shared request-defaulting logic used by post/put/delete/get/patch: fills in the domain and
    // headerType defaults when the caller didn't specify them. Also returns the resolved values
    // (typed as non-optional) for callers to use without re-triggering TS strict-null checks.
    private applyDefaultRequestOptions = (data: {
        domain?: string;
        headerType?: string;
    }): { domain: string; headerType: string } => {
        if (!data.domain) {
            data.domain = api_service.BaseDomain.Api;
        }
        if (!data.headerType) {
            data.headerType = api_service.ContentHeaders.Json;
        }
        return { domain: data.domain, headerType: data.headerType };
    };

    // Shared loader-dispatch logic used by post/put/delete/get/patch: defaults showLoader to true
    // and dispatches the loader action when needed.
    private dispatchLoaderIfNeeded = (data: { showLoader?: boolean }): void => {
        if (data.showLoader !== false) {
            data.showLoader = true;
        }
        if (data.showLoader) {
            store.dispatch(showLoader());
        }
    };

    put = (data: {
        endPoint: string;
        payLoad?: unknown;
        domain?: string;
        id?: string;
        headerType?: string;
        customHeaders?: Record<string, string>;
        showLoader?: boolean;
    }) => {
        const { domain, headerType } = this.applyDefaultRequestOptions(data);
        if (headerType === api_service.ContentHeaders.Json) {
            data.payLoad = JSON.stringify(data.payLoad);
        }
        this.dispatchLoaderIfNeeded(data);

        return axios.put(data.endPoint, data.payLoad, {
            timeout: this.axiosOptions.timeout,
            transformRequest: this.axiosOptions.transformRequest,
            baseURL: domain,
            headers: this.getHeadersByType(headerType, domain, data.customHeaders),
        });
    };

    delete = (data: {
        endPoint: string;
        payLoad?: unknown;
        domain?: string;
        id?: string;
        headerType?: string;
        customHeaders?: Record<string, string>;
        showLoader?: boolean;
    }) => {
        const { domain, headerType } = this.applyDefaultRequestOptions(data);
        this.dispatchLoaderIfNeeded(data);
        return axios.delete(data.endPoint, {
            baseURL: domain,
            headers: this.getHeadersByType(headerType, domain, data.customHeaders),
        });
    };

    get = (data: {
        endPoint: string;
        payLoad?: unknown;
        domain?: string;
        id?: string;
        headerType?: string;
        customHeaders?: Record<string, string>;
        showLoader?: boolean;
    }) => {
        const { domain, headerType } = this.applyDefaultRequestOptions(data);
        this.dispatchLoaderIfNeeded(data);
        return axios.get(data.endPoint, {
            baseURL: domain,
            timeout: this.axiosOptions.timeout,
            params: data.payLoad,
            headers: this.getHeadersByType(headerType, domain, data.customHeaders),
        });
    };
    patch = (data: {
        endPoint: string;
        payLoad?: unknown;
        domain?: string;
        id?: string;
        headerType?: string;
        customHeaders?: Record<string, string>;
        showLoader?: boolean;
    }) => {
        const payLoadData = data.payLoad instanceof FormData ? data.payLoad : JSON.stringify(data.payLoad);
        const { domain, headerType } = this.applyDefaultRequestOptions(data);
        if (headerType === api_service.ContentHeaders.Json) {
            data.payLoad = JSON.stringify(data.payLoad);
        }
        this.dispatchLoaderIfNeeded(data);

        return axios.patch(data.endPoint, payLoadData, {
            baseURL: domain,
            headers: this.getHeadersByType(headerType, domain, data.customHeaders),
        });
    };
}
export const api_service = new Server();
